import { CopyOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import { SEARCH_MODAL_KEY } from '@/components/Modal/modal-keys';
import AnimateText from '@/components/Typography/AnimatedText';
import Typo, { Span } from '@/components/Typography/Typo';
import { SummaryStatus } from '@/features/GlobalSearch/types/searchConst';
import useColor from '@/hooks/domain/useColor';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { useToast } from '@/stores/pure/ToastStore';
import { buildObjQuery, clipboardCopy } from '@/utils/util';

import SearchContentStyles from '../../SearchContentStyles';
import searchSummaryUtils from './searchSummaryUtils';

const { ContentHeader } = SearchContentStyles;

type Props = {
  searchResult: GlobalSearchResult;
  language: string;
};

const SearchSummary = (props: Props) => {
  const color = useColor();
  const { searchResult } = props;
  const { summary_issues, summary_status, alias } = searchResult;
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const addToast = useToast();
  const isKorean = props.language === 'ko';
  const { generateIssueText } = searchSummaryUtils;

  const prefix = isKorean ? '주요 사례로는' : 'Examples include';
  const suffix = isKorean ? '등이 있습니다.' : '.';

  const onCopyClick = () => {
    if (!summary_issues) return;
    const query = buildObjQuery({
      [SEARCH_MODAL_KEY]: alias,
    });
    const url = `${window.location.origin}${window.location.pathname}${query}`;
    const summaryText = generateIssueText(summary_issues, prefix, suffix);

    clipboardCopy(`${searchResult.title}\n\n${summaryText}\n${url}`);
    addToast('Copied to clipboard', 'success');
  };

  return (
    <VFlex g={24}>
      <Flex j="space-between">
        <ContentHeader>AI summary</ContentHeader>
        {summary_issues && (
          <IconButton
            icon={<CopyOutlined />}
            tooltip="Copy"
            onClick={onCopyClick}
          />
        )}
      </Flex>
      <Transition
        status={summary_status === SummaryStatus.DONE}
        fallback={
          <Flex a="center" g={6} height="40px">
            <Spinner />
            <AnimateText repeatInterval={2000}>Summarizing...</AnimateText>
          </Flex>
        }
      >
        {summary_issues ? (
          <VFlex>
            {summary_issues?.map((issue, issueIndex) => (
              <Flex a="flex-start" key={issue.index} g={12}>
                <Typo type="label2" style={{ paddingTop: 4 }}>
                  {issueIndex + 1}
                </Typo>
                <IssueBlock style={{ paddingBottom: 24 }}>
                  <Flex
                    g={4}
                    className="issue-title-block"
                    onClick={() => {
                      setNewModal<GlobalSearchSummaryFeedbackListModalOptions>({
                        alias: alias,
                        type: DETAIL_MODAL_TYPE.SEARCH_SUMMARY,
                        idx: 0,
                        keys: [issue.index],
                        options: {
                          issueIndex: issue.index,
                          title: issue.title,
                        },
                      });
                    }}
                  >
                    <Typo type="label1" weight="sb">
                      {issue.title}
                    </Typo>
                    <CountLabel className="count-label">
                      {issue.feedback_count}
                    </CountLabel>
                  </Flex>
                  <SummaryStyle>
                    <span className="issue-summary">
                      {issue.summary} {prefix}
                    </span>
                    {issue.sub_issues.map((subIssue, subIssueIndex, arr) => (
                      <SubIssueBlock
                        key={subIssue.title}
                        onClick={() => {
                          setNewModal<GlobalSearchSummaryFeedbackListModalOptions>(
                            {
                              alias: alias,
                              type: DETAIL_MODAL_TYPE.SEARCH_SUMMARY,
                              idx: 0,
                              keys: [issue.index, subIssue.index],
                              options: {
                                issueIndex: issue.index,
                                subIssueIndex: subIssue.index,
                                title: subIssue.title,
                              },
                            },
                          );
                        }}
                      >
                        <span className="sub-issue-title">
                          {subIssue.title}
                        </span>
                        &nbsp;
                        <CountLabel className="count-label">
                          {subIssue.feedback_count}
                        </CountLabel>
                        {subIssueIndex < arr.length - 1 && (
                          <span className="comma">,</span>
                        )}
                      </SubIssueBlock>
                    ))}
                    <span>{suffix}</span>
                  </SummaryStyle>
                </IssueBlock>
              </Flex>
            ))}
          </VFlex>
        ) : (
          <Flex a="center" height="40px">
            <Span type="label2" color={color.gray600}>
              It looks like there’s no feedback to generate a summary
            </Span>
          </Flex>
        )}
      </Transition>
    </VFlex>
  );
};

const SummaryStyle = styled.p`
  padding-left: 6px;
  line-height: 1.5;
  font-size: 14px;
  font-weight: 500;
  .sub-issue-title {
    font-size: 14px;
    font-weight: 500;
  }
`;

const IssueBlock = styled.div`
  .issue-title-block {
    padding: 4px 6px;
    cursor: pointer;
    transition: all 0.2s;
    width: fit-content;
    .count-label {
      background-color: ${({ theme }) => theme.colors.gray300};
      color: ${({ theme }) => theme.colors.gray700};
      transition: all 0.2s;
      transform: translateY(-1px);
    }
    &:hover {
      text-decoration: underline;
      color: ${({ theme }) => theme.colors.primary100};
      .count-label {
        background-color: ${({ theme }) => theme.colors.primary100};
        color: ${({ theme }) => theme.colors.FL100};
      }
    }
  }
`;

const SubIssueBlock = styled.span`
  padding: 3px 4px;
  cursor: pointer;
  transition: all 0.2s;
  .count-label {
    background-color: ${({ theme }) => theme.colors.gray300};
    color: ${({ theme }) => theme.colors.gray700};
    transition: all 0.2s;
  }
  &:hover {
    .sub-issue-title {
      text-decoration: underline;
      color: ${({ theme }) => theme.colors.primary100};
    }
    .count-label {
      background-color: ${({ theme }) => theme.colors.primary100};
      color: ${({ theme }) => theme.colors.FL100};
    }
  }
`;

const CountLabel = styled.span<{ selected?: boolean }>`
  padding: 2px 6px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
`;

export default SearchSummary;
